'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count: number;
}

interface FilterSection {
  title: string;
  options: FilterOption[];
}

interface FilterSidebarProps {
  filters: {
    categories: FilterOption[];
    manufacturers: FilterOption[];
    compliance: FilterOption[];
  };
  selectedFilters: {
    categories: string[];
    manufacturers: string[];
    compliance: string[];
  };
  onFilterChange: (filterType: string, value: string) => void;
  onClearAll: () => void;
}

export default function FilterSidebar({
  filters,
  selectedFilters,
  onFilterChange,
  onClearAll,
}: FilterSidebarProps) {
  const [expandedSections, setExpandedSections] = useState<string[]>([
    'categories',
    'manufacturers',
    'compliance',
  ]);

  const toggleSection = (section: string) => {
    setExpandedSections((prev) =>
      prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]
    );
  };

  const renderFilterSection = (
    title: string,
    sectionKey: string,
    options: FilterOption[]
  ) => {
    const isExpanded = expandedSections.includes(sectionKey);
    const selectedCount = selectedFilters[sectionKey as keyof typeof selectedFilters].length;

    return (
      <div className="border-b border-border pb-4 mb-4">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="flex items-center justify-between w-full text-left mb-3"
        >
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-text-primary">{title}</h3>
            {selectedCount > 0 && (
              <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded-full">
                {selectedCount}
              </span>
            )}
          </div>
          <Icon
            name="ChevronDownIcon"
            size={20}
            className={`transition-transform duration-250 ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>

        {isExpanded && (
          <div className="space-y-2">
            {options.map((option) => {
              const isSelected = selectedFilters[
                sectionKey as keyof typeof selectedFilters
              ].includes(option.id);

              return (
                <label
                  key={option.id}
                  className="flex items-center gap-3 cursor-pointer hover:bg-muted p-2 rounded transition-colors duration-250"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onFilterChange(sectionKey, option.id)}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-2 focus:ring-primary"
                  />
                  <span className="flex-1 text-sm text-text-primary">{option.label}</span>
                  <span className="text-xs text-text-secondary">({option.count})</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const totalSelected = Object.values(selectedFilters).reduce(
    (acc, curr) => acc + curr.length,
    0
  );

  return (
    <div className="bg-card border border-border rounded-lg p-5">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-text-primary">Filters</h2>
        {totalSelected > 0 && (
          <button
            onClick={onClearAll}
            className="text-sm text-primary hover:text-secondary font-medium transition-colors duration-250"
          >
            Clear All
          </button>
        )}
      </div>

      {renderFilterSection('Category', 'categories', filters.categories)}
      {renderFilterSection('Manufacturer', 'manufacturers', filters.manufacturers)}
      {renderFilterSection('Compliance', 'compliance', filters.compliance)}

      <div className="pt-4">
        <div className="bg-muted p-4 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="InformationCircleIcon" size={20} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-text-primary mb-1">Need Help?</p>
              <p className="text-xs text-text-secondary mb-3">
                Our equipment specialists can help you find the right products for your facility.
              </p>
              <button className="text-sm text-primary hover:text-secondary font-medium transition-colors duration-250">
                Contact Specialist →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}